// /home/bigbrain/projects/smashparty/src/client/ui/screens/main-menu.ts
/**
 * Plan 24a, Task 2.2 — Main Menu screen
 *
 * Card-style navigation buttons, a Settings gear, and a How to Play ghost link.
 *
 * Structure:
 *   <div class="screen-main-menu">
 *     <button aria-label="Settings"> (top-right absolute)
 *     <div class="main-menu__grid"> (2x2 grid)
 *       <button data-screen-card="create-game">
 *       <button data-screen-card="join-game">
 *       <button data-screen-card="quick-play">
 *       <button data-screen-card="minigames">
 *     </div>
 *     <button class="main-menu__how-to-play">How to Play</button>
 *   </div>
 *
 * Design system references:
 *   - docs/design/tokens.json — color.menu-cards.*, layout.menu-card-*, border-radius.xl
 *   - docs/design/components.md §1 — Menu Card Buttons (lines 92–103)
 *   - docs/design/screens.md — Main Menu (Screen 2)
 */

import { createButton } from '../components/button';
import type { ScreenHandle, ScreenMountContext } from './types';
import {
  buildPortalReturnUrl,
  readPreferredCustomization,
  type PortalMainMenuPayload,
} from '../../portal-entry';
import { openJoinRoomModal, type JoinRoomModalHandle } from './join-room-modal';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PLAYER_NAME_KEY = 'smashparty_player_name';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type MainMenuPayload = {
  send: (msg: unknown) => void;
  subscribe: (handler: (msg: unknown) => void) => () => void;
  characterAdapter?: import('./character-preview-adapter').CharacterPreviewAdapter | null;
  portal?: PortalMainMenuPayload;
};

interface MenuCardConfig {
  key: string;
  label: string;
  icon: string;
  gradTop: string;
  gradBottom: string;
  border: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Card definitions (tokens from docs/design/tokens.json color.menu-cards.*)
// ─────────────────────────────────────────────────────────────────────────────

const MENU_CARDS: MenuCardConfig[] = [
  {
    key: 'solo-minigame',
    label: 'SOLO MINIGAME',
    icon: '🎮',
    gradTop: '#D85878',
    gradBottom: '#9C2E4E',
    border: '#641D34',
  },
  {
    key: 'create-game',
    label: 'CREATE GAME',
    icon: '👑',
    gradTop: '#F0B838',
    gradBottom: '#C4841C',
    border: '#8A5A10',
  },
  {
    key: 'join-game',
    label: 'JOIN GAME',
    icon: '🔗',
    gradTop: '#5CA0E8',
    gradBottom: '#3A6FB0',
    border: '#264A78',
  },
  {
    key: 'quick-play',
    label: 'QUICK PLAY',
    icon: '⚡',
    gradTop: '#6CC050',
    gradBottom: '#488A30',
    border: '#2E5E1C',
  },
  {
    key: 'minigames',
    label: 'MINIGAMES ONLY',
    icon: '🎮',
    gradTop: '#F0A040',
    gradBottom: '#C07020',
    border: '#8A5018',
  },
];

const PORTAL_MENU_CARDS: MenuCardConfig[] = [
  {
    key: 'solo-minigame',
    label: 'PLAY SOLO MINIGAME',
    icon: '🎮',
    gradTop: '#D85878',
    gradBottom: '#9C2E4E',
    border: '#641D34',
  },
  {
    key: 'quick-play',
    label: 'QUICK MATCH',
    icon: '⚔️',
    gradTop: '#6CC050',
    gradBottom: '#488A30',
    border: '#2E5E1C',
  },
  {
    key: 'return-portal',
    label: 'RETURN PORTAL',
    icon: '↗',
    gradTop: '#5CA0E8',
    gradBottom: '#3A6FB0',
    border: '#264A78',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// createMenuCard — plain <button> element styled as a card (NOT createButton)
// ─────────────────────────────────────────────────────────────────────────────

function createMenuCard(cfg: MenuCardConfig): HTMLButtonElement {
  const card = document.createElement('button');
  card.type = 'button';
  card.setAttribute('data-screen-card', cfg.key);

  card.style.cssText = [
    // Size: 240px × 100px per tokens.json layout.menu-card-width/height
    `width: var(--layout-menu-card-width, 240px)`,
    `height: var(--layout-menu-card-height, 100px)`,
    // Vertical flex: icon upper-left, label below center
    `display: flex`,
    `flex-direction: column`,
    `align-items: flex-start`,
    `justify-content: flex-end`,
    `padding: 10px 14px 12px`,
    // Gradient
    `background: linear-gradient(to bottom, ${cfg.gradTop}, ${cfg.gradBottom})`,
    // Border: 2px solid per card color
    `border: 2px solid ${cfg.border}`,
    // Border radius: border-radius.xl = 16px
    `border-radius: var(--border-radius-xl, 16px)`,
    // Shadow
    `box-shadow: var(--shadow-button, 0 4px 0 rgba(0,0,0,0.50))`,
    `cursor: pointer`,
    `outline: none`,
    `overflow: hidden`,
    `position: relative`,
    `box-sizing: border-box`,
    `transition: filter 150ms cubic-bezier(0.2,0,0,1)`,
    `text-align: left`,
  ].join('; ');

  // Icon circle (upper-left)
  const iconCircle = document.createElement('div');
  iconCircle.style.cssText = [
    `position: absolute`,
    `top: 10px`,
    `left: 12px`,
    `width: 28px`,
    `height: 28px`,
    `border-radius: 50%`,
    `background: rgba(0,0,0,0.15)`,
    `display: flex`,
    `align-items: center`,
    `justify-content: center`,
    `font-size: 15px`,
    `line-height: 1`,
    `pointer-events: none`,
    `user-select: none`,
  ].join('; ');
  iconCircle.textContent = cfg.icon;
  iconCircle.setAttribute('aria-hidden', 'true');
  card.appendChild(iconCircle);

  // Label text
  const labelEl = document.createElement('span');
  labelEl.textContent = cfg.label;
  labelEl.style.cssText = [
    `font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)`,
    `font-size: var(--typography-size-h5, 18px)`,
    `font-weight: var(--typography-weight-bold, 700)`,
    `color: #FFFFFF`,
    `text-transform: uppercase`,
    `letter-spacing: var(--typography-letter-spacing-headline, 1px)`,
    `text-shadow: 0 1px 0 #000000, 0 2px 6px rgba(0,0,0,0.50)`,
    `pointer-events: none`,
    `user-select: none`,
    `display: block`,
    `width: 100%`,
  ].join('; ');
  card.appendChild(labelEl);

  return card;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createMainMenuScreen(ctx: ScreenMountContext): ScreenHandle {
  const { gsapRegistry, host, router } = ctx;
  const { send, subscribe, characterAdapter, portal } = (ctx.payload ?? {}) as MainMenuPayload;
  const isPortalVariant = portal !== undefined;

  let disposed = false;
  let bannerTimer: number | null = null;

  let joinModalHandle: JoinRoomModalHandle | null = null;

  // ── Root element ─────────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-main-menu';
  root.style.cssText = [
    `position: relative`,
    `width: 100%`,
    `height: 100%`,
    `display: flex`,
    `flex-direction: column`,
    `align-items: center`,
    `justify-content: center`,
    `overflow: hidden`,
    `box-sizing: border-box`,
  ].join('; ');

  // ── Settings gear (top-right absolute) ───────────────────────────────────────

  const settingsBtnHandle = createButton({
    gsapRegistry,
    variant: 'secondary',
    size: 'icon',
    label: '⚙',
    onClick: () => router.navigate('settings'),
  });
  const settingsEl = settingsBtnHandle.element;
  settingsEl.setAttribute('aria-label', 'Settings');
  settingsEl.style.position = 'absolute';
  settingsEl.style.top = 'var(--spacing-md, 16px)';
  settingsEl.style.right = 'var(--spacing-md, 16px)';
  settingsEl.style.width = '64px';
  settingsEl.style.height = '64px';
  settingsEl.style.minWidth = '64px';
  settingsEl.style.minHeight = '64px';
  settingsEl.style.fontSize = '32px';
  settingsEl.style.borderRadius = '50%';
  settingsEl.style.padding = '0';
  settingsEl.style.display = 'flex';
  settingsEl.style.alignItems = 'center';
  settingsEl.style.justifyContent = 'center';
  root.appendChild(settingsEl);

  // ── 2×2 card grid ─────────────────────────────────────────────────────────────
  let bannerEl: HTMLDivElement | null = null;
  if (isPortalVariant) {
    bannerEl = document.createElement('div');
    bannerEl.className = 'main-menu__portal-banner';
    bannerEl.textContent = `Welcome from ${portal.ref ?? 'the Vibe Jam portal'}!`;
    bannerEl.style.cssText = [
      'margin-bottom: 18px',
      'padding: 10px 18px',
      'border: 2px solid rgba(255,255,255,0.16)',
      'border-radius: 8px',
      'background: rgba(30,35,50,0.88)',
      'box-shadow: 0 5px 16px rgba(0,0,0,0.35)',
      'color: #FFFFFF',
      `font-family: var(--typography-family-headline, 'Fredoka One', 'Arial Black', sans-serif)`,
      'font-size: 22px',
      'font-weight: 800',
      'text-shadow: 0 2px 0 #000000',
    ].join('; ');
    root.appendChild(bannerEl);
    bannerTimer = window.setTimeout(() => {
      dismissPortalBanner();
    }, 5000);
  }

  const grid = document.createElement('div');
  grid.className = 'main-menu__grid';
  const activeCards = isPortalVariant ? PORTAL_MENU_CARDS : MENU_CARDS;
  const gridColumnCount = isPortalVariant ? activeCards.length : 3;
  grid.style.cssText = [
    `display: grid`,
    `grid-template-columns: repeat(${gridColumnCount}, var(--layout-menu-card-width, 240px))`,
    `grid-auto-rows: var(--layout-menu-card-height, 100px)`,
    `gap: var(--layout-menu-card-gap, 16px)`,
  ].join('; ');
  root.appendChild(grid);

  // Create menu cards
  const cards: HTMLButtonElement[] = [];
  for (const cfg of activeCards) {
    const card = createMenuCard(cfg);
    grid.appendChild(card);
    cards.push(card);
  }

  // ── How to Play ghost link ────────────────────────────────────────────────────

  const howToPlayBtn = document.createElement('button');
  howToPlayBtn.className = 'main-menu__how-to-play';
  howToPlayBtn.type = 'button';
  howToPlayBtn.textContent = 'How to Play';
  howToPlayBtn.style.cssText = [
    `margin-top: var(--spacing-lg, 24px)`,
    `background: transparent`,
    `border: none`,
    `color: var(--color-text-secondary, #B0B8C8)`,
    `font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)`,
    `font-size: var(--typography-size-body, 16px)`,
    `cursor: pointer`,
    `text-decoration: underline`,
    `opacity: 0.7`,
    `padding: 4px 8px`,
    `outline: none`,
  ].join('; ');
  if (!isPortalVariant) {
    root.appendChild(howToPlayBtn);
  }

  // ── Stagger animation for cards ───────────────────────────────────────────────

  const staggerHandle = gsapRegistry.create('main-menu:stagger');
  cards.forEach((card, i) => {
    staggerHandle.timeline.fromTo(
      card,
      { x: 60, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.3, delay: i * 0.04 },
      0,
    );
  });

  // ── Mount root into host ──────────────────────────────────────────────────────

  host.appendChild(root);

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  function getPlayerName(): string {
    return localStorage.getItem(PLAYER_NAME_KEY) ?? '';
  }

  function getLobbyPayloadBase(): {
    playerName: string;
    send: (msg: unknown) => void;
    subscribe: (handler: (msg: unknown) => void) => () => void;
    characterAdapter: typeof characterAdapter;
    preferredCustomization?: ReturnType<typeof readPreferredCustomization>;
  } {
    const preferredCustomization = readPreferredCustomization();
    return {
      playerName: getPlayerName(),
      send,
      subscribe,
      characterAdapter,
      ...(preferredCustomization == null ? {} : { preferredCustomization }),
    };
  }

  function dismissPortalBanner(): void {
    if (bannerTimer != null) {
      window.clearTimeout(bannerTimer);
      bannerTimer = null;
    }
    bannerEl?.remove();
    bannerEl = null;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Card click handlers
  // ─────────────────────────────────────────────────────────────────────────────

  function handleCreateGameClick(): void {
    router.navigate('lobby-host', {
      ...getLobbyPayloadBase(),
      mode: 'board_game',
      roomType: 'create_game',
    });
  }

  function handleJoinGameClick(): void {
    joinModalHandle?.dispose();
    const base = getLobbyPayloadBase();
    joinModalHandle = openJoinRoomModal({
      mount: root,
      send: base.send,
      subscribe: (handler) =>
        base.subscribe((msg) => handler(msg as Parameters<typeof handler>[0])),
      playerName: base.playerName,
      ...(base.preferredCustomization == null
        ? {}
        : { preferredCustomization: base.preferredCustomization }),
      onAccepted: (joined) => {
        joinModalHandle = null;
        router.navigate('lobby-joiner', {
          ...base,
          mode: joined.mode === 'minigames_only' ? 'minigames_only' : 'board_game',
          roomType: joined.roomType,
          code: joined.roomCode,
          skipInitialRequest: true,
          restoredSession: {
            playerId: joined.playerId,
            hostId: joined.hostId,
            roomCode: joined.roomCode,
          },
        });
      },
      onCancel: () => {
        joinModalHandle = null;
      },
    });
  }

  function handleQuickPlayClick(): void {
    dismissPortalBanner();
    router.navigate('lobby-quick-play', {
      ...getLobbyPayloadBase(),
      mode: 'board_game',
      roomType: 'quick_play',
    });
  }

  function handleMinigamesClick(): void {
    router.navigate('lobby-host', {
      ...getLobbyPayloadBase(),
      mode: 'minigames_only',
      roomType: 'create_game',
    });
  }

  function handleSoloMinigameClick(): void {
    dismissPortalBanner();
    const preferredCustomization = readPreferredCustomization();
    send({
      type: 'start_solo_minigame',
      playerName: getPlayerName(),
      ...(preferredCustomization == null ? {} : { customization: preferredCustomization }),
    });
  }

  function handleReturnPortalClick(): void {
    dismissPortalBanner();
    if (portal === undefined) return;
    window.location.assign(buildPortalReturnUrl(portal));
  }

  function handleHowToPlayClick(): void {
    router.navigate('how-to-play', {
      onBack: () => router.navigate('warmup-arena-overlay', { portal: portal ?? null }),
    });
  }

  // Map data-screen-card keys to handlers
  const cardHandlers: Record<string, () => void> = {
    'solo-minigame': handleSoloMinigameClick,
    'create-game': handleCreateGameClick,
    'join-game': handleJoinGameClick,
    'quick-play': handleQuickPlayClick,
    minigames: handleMinigamesClick,
    'return-portal': handleReturnPortalClick,
  };

  // Attach click listeners to cards
  for (const card of cards) {
    const key = card.getAttribute('data-screen-card') ?? '';
    const handler = cardHandlers[key];
    if (handler) {
      card.addEventListener('click', handler);
    }
  }

  // Attach how-to-play handler
  howToPlayBtn.addEventListener('click', handleHowToPlayClick);

  // ─────────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove card click listeners
    for (const card of cards) {
      const key = card.getAttribute('data-screen-card') ?? '';
      const handler = cardHandlers[key];
      if (handler) {
        card.removeEventListener('click', handler);
      }
    }

    // Remove how-to-play listener
    howToPlayBtn.removeEventListener('click', handleHowToPlayClick);
    dismissPortalBanner();
    joinModalHandle?.dispose();
    joinModalHandle = null;

    // Dispose settings button
    settingsBtnHandle.dispose();

    // Dispose stagger timeline
    staggerHandle.dispose();

    // Remove root from host
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
